// src/pages/PaymentManagement/PaymentList.js
import React, { useState, useEffect } from 'react';
import { paymentService } from '../../services/paymentService';
import { planService } from '../../services/planService';
import { useLanguage } from '../../contexts/LanguageContext';
import { Eye, CheckCircle, XCircle, Search, Filter } from 'lucide-react';

const PaymentList = () => {
    const [payments, setPayments] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        status: '',
        plan: '',
        date_from: '',
        date_to: ''
    });
    const { t, language } = useLanguage();

    useEffect(() => {
        loadPayments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchTerm, filters]);

    useEffect(() => {
        planService.getAllPlans().then(response => {
            if (response.success) setPlans((response.data.plans || []).filter(p => !p.isSystem));
        }).catch(() => { });
    }, []);

    const loadPayments = async () => {
        try {
            setLoading(true);
            const response = await paymentService.getAllPayments(currentPage, 20, {
                ...filters,
                search: searchTerm
            });

            if (response.success) {
                setPayments(response.data.payments || []);
                setTotalPages(response.data.pagination?.pages || 1);
            }
        } catch (error) {
            console.error(t('loadPaymentsError'), error);
            setPayments([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const handleApprovePayment = async (paymentId) => {
        try {
            await paymentService.approvePayment(paymentId);
            loadPayments(); // Listeyi yenile
        } catch (error) {
            console.error(t('approvePaymentError'), error);
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'completed': return 'completed';
            case 'pending': return 'pending';
            case 'failed': return 'failed';
            default: return 'pending';
        }
    };

    // Paketler artık admin panelden dinamik oluşturuluyor - sabit free/silver/gold switch'i
    // yeni bir paketi yanlışlıkla "free" gibi gösterirdi. Slug'ı doğrudan class adı olarak
    // kullanıyoruz; scss bilinen 3 paket için zaten .free/.silver/.gold tanımlıyor.
    const getPlanBadgeClass = (plan) => plan || 'free';

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount, currency) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: currency === 'TL' ? 'TRY' : currency,
        }).format(amount);
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div>{t('loading')}</div>
            </div>
        );
    }

    return (
        <div className="payment-list">
            <div className="page-header">
                <h1>{t('allPayments')}</h1>
                <p>{t('allPaymentsDescription')}</p>
            </div>

            <div className="payment-content">
                {/* Filtreler */}
                <div className="filters-section">
                    <div className="search-box">
                        <Search size={18} />
                        <input
                            type="text"
                            placeholder={t('searchPayments')}
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>

                    <div className="filter-controls">
                        <select
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                        >
                            <option value="">{t('allStatuses')}</option>
                            <option value="completed">{t('completed')}</option>
                            <option value="pending">{t('pending')}</option>
                            <option value="failed">{t('failed')}</option>
                        </select>

                        <select
                            value={filters.plan}
                            onChange={(e) => handleFilterChange('plan', e.target.value)}
                        >
                            <option value="">{t('allPlans')}</option>
                            {plans.map(plan => (
                                <option key={plan.slug} value={plan.slug}>
                                    {language === 'tr' ? plan.nameTr : plan.nameEn}
                                </option>
                            ))}
                        </select>

                        <input
                            type="date"
                            placeholder="Başlangıç Tarihi"
                            value={filters.date_from}
                            onChange={(e) => handleFilterChange('date_from', e.target.value)}
                        />

                        <input
                            type="date"
                            placeholder="Bitiş Tarihi"
                            value={filters.date_to}
                            onChange={(e) => handleFilterChange('date_to', e.target.value)}
                        />
                    </div>
                </div>

                {/* Ödemeler Tablosu */}
                <div className="payments-table-container">
                    <table className="payments-table">
                        <thead>
                            <tr>
                                <th>{t('id')}</th>
                                <th>{t('user')}</th>
                                <th>{t('plan')}</th>
                                <th>{t('amount')}</th>
                                <th>{t('status')}</th>
                                <th>{t('paymentDate')}</th>
                                <th>{t('actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payments.map(payment => (
                                <tr key={payment.id}>
                                    <td>#{payment.id}</td>
                                    <td>
                                        <div className="user-info">
                                            <div className="user-name">{payment.name}</div>
                                            <div className="user-email">{payment.email}</div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`plan-badge ${getPlanBadgeClass(payment.plan)}`}>
                                            {payment.plan}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="amount">
                                            {formatCurrency(payment.amount, payment.currency)}
                                        </div>
                                        <div className="billing-period">
                                            {payment.billing_period}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${getStatusBadgeClass(payment.payment_status)}`}>
                                            {payment.payment_status}
                                        </span>
                                    </td>
                                    <td>{formatDate(payment.created_at)}</td>
                                    <td>
                                        <div className="payment-actions">
                                            <button
                                                className="view-btn"
                                                title={t('viewDetails')}
                                            >
                                                <Eye size={14} />
                                            </button>
                                            {payment.payment_status === 'pending' && (
                                                <button
                                                    className="approve-btn"
                                                    onClick={() => handleApprovePayment(payment.id)}
                                                    title={t('approvePayment')}
                                                >
                                                    <CheckCircle size={14} />
                                                </button>
                                            )}
                                            {payment.payment_status === 'pending' && (
                                                <button
                                                    className="reject-btn"
                                                    title={t('rejectPayment')}
                                                >
                                                    <XCircle size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {payments.length === 0 && !loading && (
                        <div className="no-data">
                            <p>{t('noPaymentsFound')}</p>
                        </div>
                    )}

                    {/* Sayfalama */}
                    <div className="pagination">
                        <div className="pagination-info">
                            {t('page')} {currentPage} / {totalPages}
                        </div>
                        <div className="pagination-controls">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => prev - 1)}
                            >
                                {t('previous')}
                            </button>
                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                            >
                                {t('next')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentList;